/**
 * WeaveIn.ai Platform - ECM Reminders Hook
 * React hook for ECM notification management (compliance dashboard, snooze, config)
 */

import { useState, useEffect, useCallback } from 'react';
import {
  ecmRemindersAPI,
  MemberContactStatus,
  ComplianceSummary,
  ComplianceDashboardParams,
  ContactStatus,
  Snooze,
  SnoozeCreateRequest,
  BulkAcknowledgeRequest,
  BulkAcknowledgeResponse,
  ProgramReminderConfig,
  ProgramReminderConfigUpdate,
  MemberNotificationPreferences,
  MemberNotificationPreferencesUpdate,
  BulkNotificationPreferencesUpdate,
  BulkPreferencesResponse,
} from '@/services/api/ecmReminders';

// ============================================================================
// Compliance Dashboard Hook
// ============================================================================

interface UseComplianceDashboardOptions {
  staff_user_id?: number;
  contact_status?: ContactStatus;
  include_snoozed?: boolean;
  page?: number;
  page_size?: number;
  autoFetch?: boolean;
}

interface UseComplianceDashboardReturn {
  members: MemberContactStatus[];
  summary: ComplianceSummary | null;
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
  setFilters: (filters: Partial<ComplianceDashboardParams>) => void;
}

export function useComplianceDashboard(
  options: UseComplianceDashboardOptions = {}
): UseComplianceDashboardReturn {
  const {
    staff_user_id: initialStaffId,
    contact_status: initialStatus,
    include_snoozed: initialIncludeSnoozed = true,
    page: initialPage = 1,
    page_size = 50,
    autoFetch = true,
  } = options;

  const [members, setMembers] = useState<MemberContactStatus[]>([]);
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPageState] = useState(initialPage);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<Partial<ComplianceDashboardParams>>({
    staff_user_id: initialStaffId,
    contact_status: initialStatus,
    include_snoozed: initialIncludeSnoozed,
  });

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ecmRemindersAPI.getComplianceDashboard({
        ...filters,
        page,
        page_size,
      });
      setMembers(response.members);
      setSummary(response.summary);
      setTotal(response.total);
      setHasMore(response.has_more);
    } catch (err) {
      console.error('Error fetching compliance dashboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [filters, page, page_size]);

  useEffect(() => {
    if (autoFetch) {
      fetchDashboard();
    }
  }, [fetchDashboard, autoFetch]);

  const setPage = (newPage: number) => setPageState(newPage);

  const setFilters = (newFilters: Partial<ComplianceDashboardParams>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
    setPageState(1);
  };

  return {
    members,
    summary,
    total,
    page,
    pageSize: page_size,
    hasMore,
    loading,
    error,
    refetch: fetchDashboard,
    setPage,
    setFilters,
  };
}

// ============================================================================
// Snooze Management Hook
// ============================================================================

interface UseSnoozeReturn {
  snoozes: Snooze[];
  loading: boolean;
  error: string | null;
  createSnooze: (data: SnoozeCreateRequest) => Promise<Snooze | null>;
  cancelSnooze: (snoozeId: number) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useSnoozes(memberId?: number): UseSnoozeReturn {
  const [snoozes, setSnoozes] = useState<Snooze[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSnoozes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ecmRemindersAPI.listSnoozes({
        member_id: memberId,
        active_only: true,
      });
      setSnoozes(response.snoozes);
    } catch (err) {
      console.error('Error fetching snoozes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load snoozes');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchSnoozes();
  }, [fetchSnoozes]);

  const createSnooze = async (data: SnoozeCreateRequest): Promise<Snooze | null> => {
    try {
      const snooze = await ecmRemindersAPI.createSnooze(data);
      await fetchSnoozes();
      return snooze;
    } catch (err) {
      console.error('Error creating snooze:', err);
      setError(err instanceof Error ? err.message : 'Failed to create snooze');
      return null;
    }
  };

  const cancelSnooze = async (snoozeId: number): Promise<boolean> => {
    try {
      await ecmRemindersAPI.cancelSnooze(snoozeId);
      await fetchSnoozes();
      return true;
    } catch (err) {
      console.error('Error cancelling snooze:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel snooze');
      return false;
    }
  };

  return { snoozes, loading, error, createSnooze, cancelSnooze, refetch: fetchSnoozes };
}

// ============================================================================
// Bulk Acknowledge Hook
// ============================================================================

export function useBulkAcknowledge() {
  const [loading, setLoading] = useState(false);

  const acknowledge = async (
    params: BulkAcknowledgeRequest
  ): Promise<BulkAcknowledgeResponse | null> => {
    setLoading(true);
    try {
      const result = await ecmRemindersAPI.bulkAcknowledge(params);
      return result;
    } catch (err) {
      console.error('Error bulk acknowledging:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { acknowledge, loading };
}

// ============================================================================
// Reminder Config Hook (Program-Level)
// ============================================================================

interface UseReminderConfigReturn {
  config: ProgramReminderConfig | null;
  loading: boolean;
  error: string | null;
  updateConfig: (data: ProgramReminderConfigUpdate) => Promise<ProgramReminderConfig | null>;
  refetch: () => Promise<void>;
}

export function useReminderConfig(programId: number | undefined): UseReminderConfigReturn {
  const [config, setConfig] = useState<ProgramReminderConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    if (!programId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await ecmRemindersAPI.getReminderConfig(programId);
      setConfig(data);
    } catch (err) {
      console.error('Error fetching reminder config:', err);
      setError(err instanceof Error ? err.message : 'Failed to load config');
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const updateConfig = async (
    data: ProgramReminderConfigUpdate
  ): Promise<ProgramReminderConfig | null> => {
    if (!programId) return null;
    try {
      const updated = await ecmRemindersAPI.updateReminderConfig(programId, data);
      setConfig(updated);
      return updated;
    } catch (err) {
      console.error('Error updating config:', err);
      setError(err instanceof Error ? err.message : 'Failed to update config');
      return null;
    }
  };

  return { config, loading, error, updateConfig, refetch: fetchConfig };
}


// ============================================================================
// Member Notification Preferences Hook
// ============================================================================

interface UseMemberNotificationPreferencesReturn {
  preferences: MemberNotificationPreferences | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  updatePreferences: (data: MemberNotificationPreferencesUpdate) => Promise<MemberNotificationPreferences | null>;
  refetch: () => Promise<void>;
}

export function useMemberNotificationPreferences(
  memberId: number | undefined
): UseMemberNotificationPreferencesReturn {
  const [preferences, setPreferences] = useState<MemberNotificationPreferences | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPreferences = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await ecmRemindersAPI.getMemberPreferences(memberId);
      setPreferences(data);
    } catch (err) {
      console.error('Error fetching member notification prefs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const updatePreferences = async (
    data: MemberNotificationPreferencesUpdate
  ): Promise<MemberNotificationPreferences | null> => {
    if (!memberId) return null;
    setSaving(true);
    try {
      const updated = await ecmRemindersAPI.updateMemberPreferences(memberId, data);
      setPreferences(updated);
      return updated;
    } catch (err) {
      console.error('Error updating member notification prefs:', err);
      setError(err instanceof Error ? err.message : 'Failed to update preferences');
      return null;
    } finally {
      setSaving(false);
    }
  };

  return { preferences, loading, saving, error, updatePreferences, refetch: fetchPreferences };
}


// ============================================================================
// Bulk Notification Preferences Hook
// ============================================================================

interface UseBulkNotificationPreferencesReturn {
  updating: boolean;
  error: string | null;
  bulkUpdate: (data: BulkNotificationPreferencesUpdate) => Promise<BulkPreferencesResponse | null>;
}

export function useBulkNotificationPreferences(): UseBulkNotificationPreferencesReturn {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bulkUpdate = async (
    data: BulkNotificationPreferencesUpdate
  ): Promise<BulkPreferencesResponse | null> => {
    setUpdating(true);
    setError(null);
    try {
      const result = await ecmRemindersAPI.bulkUpdatePreferences(data);
      return result;
    } catch (err) {
      console.error('Error in bulk notification preference update:', err);
      setError(err instanceof Error ? err.message : 'Failed to update preferences');
      return null;
    } finally {
      setUpdating(false);
    }
  };

  return { updating, error, bulkUpdate };
}
